// apps-events.js

import state from './apps-state.js';
import * as nav from './apps-navigation.js';
import * as folder from './apps-folders.js';
import * as iconops from './apps-icons.js';
import * as cursor from './apps-kb-cursor.js';

// ===== TIMING CONSTANTS =====
const PREVIEW_DELAY = 80;        // Show drag preview
const SWIPE_TIME_THRESHOLD = 80;  // Max time for quick swipe
const SWIPE_DISTANCE = 20;        // Min distance for swipe

// Global press state storage - keyed by icon ID
const iconPressStates = new Map();

function getPressState(iconId) {
    if (!iconPressStates.has(iconId)) {
        iconPressStates.set(iconId, createPressStateManager());
    }
    return iconPressStates.get(iconId);
}

// ===== GHOST MANAGEMENT =====
export function createDragGhost(iconEl) {
    const computedIconSize = parseInt(
        getComputedStyle(document.documentElement)
            .getPropertyValue('--icon-size')
    ) || state.config.iconSize;
    
    const rect = iconEl.getBoundingClientRect();
    
    const ghost = iconEl.cloneNode(true);
    ghost.style.position = 'fixed';
    ghost.style.left = rect.left + 'px';
    ghost.style.top = rect.top + 'px';
    ghost.style.width = computedIconSize + 'px';
    ghost.style.height = computedIconSize + 'px';
    ghost.style.zIndex = '10000';
    ghost.style.pointerEvents = 'none';
    ghost.style.transition = 'transform 0.2s ease-out';
    ghost.style.transform = 'scale(1.15)';
    ghost.classList.add('dragging');
    
    document.body.appendChild(ghost);
    return ghost;
}

export function cleanupDragGhost(iconEl) {
    if (state.dragGhost) {
        state.dragGhost.remove();
        state.dragGhost = null;
    }
    iconEl.closest('.icon-slot')?.classList.remove('drag-placeholder');
}

// ===== SCREEN DRAG MANAGEMENT =====
export function stopScreenDragging() {
    state.isDraggingScreen = false;
    const screensContainer = state.openFolder ?
        document.getElementById('folderScreens') :
        document.getElementById('appspaceScreens');
    
    if (screensContainer) {
        screensContainer.classList.remove('dragging');
        if (state.openFolder) {
            nav.goToFolderScreen(state.currentFolderScreen);
        } else {
            nav.goToScreen(state.currentScreen);
        }
    }
}

export function enableScreenDragging() {
    if (state.openFolder) {
        state.folderDragState.isDragging = true;
        state.folderDragState.startScreen = state.currentFolderScreen;
        document.getElementById('folderScreens').classList.add('dragging');
    } else {
        state.isDraggingScreen = true;
    }
}

// ===== FOLDER DRAG CLEANUP =====
export function cleanupFolderDrag() {
    if (state.folderDragState.isDragging) {
        state.folderDragState.isDragging = false;
        const folderScreensContainer = document.getElementById('folderScreens');
        if (folderScreensContainer) {
            folderScreensContainer.classList.remove('dragging');
            nav.goToFolderScreen(state.currentFolderScreen);
        }
    }
}

// ===== ICON ACTIONS =====
export function handleIconClick(icon) {
    cursor.hideVirtualCursor();
    
    if (icon.type === 'folder') {
        folder.openFolderView(icon);
    } else if (icon.url) {
        window.location.href = icon.url;
    }
}

export function handleLongPress(icon, iconEl) {
    cleanupDragGhost(iconEl);
    
    if (icon.type === 'app' && !state.openFolder) {
        iconops.openEditModal(icon);
    } else if (icon.type === 'folder' && !state.openFolder) {
        folder.openFolderView(icon);
    } else if (icon.type === 'app' && state.openFolder) {
        iconops.openEditModal(icon);
    }
}

// ===== SCROLL ZONE ACTIVATION =====
export function activateScrollZones() {
    // On temporary screens, only show scroll zone to return to real screen
    const onLeftTemporary = state.temporaryScreenSide === 'left';
    const onRightTemporary = state.temporaryScreenSide === 'right';
    
    if (state.openFolder) {
        const leftZone = document.getElementById('folderScrollZoneLeft');
        const rightZone = document.getElementById('folderScrollZoneRight');
        
        // Only show right zone on left temporary (to go back right)
        if (onLeftTemporary) {
            if (rightZone) rightZone.classList.add('active');
        }
        // Only show left zone on right temporary (to go back left)
        else if (onRightTemporary) {
            if (leftZone) leftZone.classList.add('active');
        }
        // Show both zones on normal screens
        else {
            if (leftZone) leftZone.classList.add('active');
            if (rightZone) rightZone.classList.add('active');
        }
    } else {
        const leftZone = document.getElementById('scrollZoneLeft');
        const rightZone = document.getElementById('scrollZoneRight');
        
        // Only show right zone on left temporary (to go back right)
        if (onLeftTemporary) {
            if (rightZone) rightZone.classList.add('active');
        }
        // Only show left zone on right temporary (to go back left)
        else if (onRightTemporary) {
            if (leftZone) leftZone.classList.add('active');
        }
        // Show both zones on normal screens
        else {
            if (leftZone) leftZone.classList.add('active');
            if (rightZone) rightZone.classList.add('active');
        }
    }
}

// ===== DRAG INITIATION =====
export function initiateDrag(icon, iconEl, touch) {
    state.setDraggingState(true);
    const rect = iconEl.getBoundingClientRect();
    
    state.dragState = {
        icon: icon,
        originalRegion: icon.region,
        originalPosition: icon.position,
        originalFolder: icon.kfolder,
        currentX: touch.clientX,
        currentY: touch.clientY,
        offsetX: touch.clientX - rect.left - rect.width / 2,
        offsetY: touch.clientY - rect.top - rect.height / 2
    };
    
    // Remove transition for smooth dragging
    state.dragGhost.style.transition = 'none';
    
    activateScrollZones();
}

// ===== MAIN ATTACHMENT FUNCTION =====
// Store listener references
const listenerMap = new WeakMap();

let attachCount = 0;

export function attachIconEventListeners(containerSelector = null) {
    const selector = containerSelector ? `${containerSelector} .appicon` : '.appicon';
    
    document.querySelectorAll(selector).forEach(iconEl => {
        // Remove old listeners if they exist
        if (listenerMap.has(iconEl)) {
            const oldListeners = listenerMap.get(iconEl);
            iconEl.removeEventListener('mousedown', oldListeners.mousedown);
            iconEl.removeEventListener('touchstart', oldListeners.touchstart);
            iconEl.removeEventListener('mousemove', oldListeners.mousemove);
            iconEl.removeEventListener('touchmove', oldListeners.touchmove);
            iconEl.removeEventListener('mouseup', oldListeners.mouseup);
            iconEl.removeEventListener('touchend', oldListeners.touchend);
            iconEl.removeEventListener('mouseleave', oldListeners.mouseleave);
            iconEl.removeEventListener('contextmenu', oldListeners.contextmenu);
        }
        
        // Get or create press state for this icon ID
        const iconId = parseInt(iconEl.dataset.iconId);
        const pressState = getPressState(iconId);
        
        // Create listener functions
        const listeners = {
            mousedown: (e) => handlePress(e, iconEl, pressState),
            touchstart: (e) => handlePress(e, iconEl, pressState),
            mousemove: (e) => handleMove(e, iconEl, pressState),
            touchmove: (e) => handleMove(e, iconEl, pressState),
            mouseup: (e) => handleRelease(e, iconEl, pressState),
            touchend: (e) => handleRelease(e, iconEl, pressState),
            mouseleave: () => handleLeave(iconEl, pressState),
            contextmenu: (e) => handleContextMenu(e, iconEl)
        };
        
        // Store listeners for cleanup
        listenerMap.set(iconEl, listeners);
        
        // Attach listeners
        iconEl.addEventListener('mousedown', listeners.mousedown);
        iconEl.addEventListener('touchstart', listeners.touchstart, { passive: true });
        iconEl.addEventListener('mousemove', listeners.mousemove);
        iconEl.addEventListener('touchmove', listeners.touchmove, { passive: false });
        iconEl.addEventListener('mouseup', listeners.mouseup);
        iconEl.addEventListener('touchend', listeners.touchend);
        iconEl.addEventListener('mouseleave', listeners.mouseleave);
        iconEl.addEventListener('contextmenu', listeners.contextmenu);
    });
}

// ===== PRESS STATE MANAGER =====
function createPressStateManager() {
    return {
        startX: 0,
        startY: 0,
        hasDragged: false,
        dragStartTime: 0,
        pressTimer: null,
        isPressed: false,
        pressedOnThisIcon: false,
        wasRightClick: false
    };
}

// ===== EVENT HANDLERS =====
function handlePress(e, iconEl, pressState) {
    if (state.isDraggingIcon) return;
    
    // Mark if this is a right-click, then ignore it
    if (e.button === 2) {
        pressState.wasRightClick = true;
        return;
    }
    
    // Clear right-click flag for left clicks
    pressState.wasRightClick = false;
    
    // Prevent conflicting interactions
    state.isDraggingScreen = false;
    state.isInteractingWithIcon = true;
    pressState.pressedOnThisIcon = true;
    
    cleanupFolderDrag();
    
    const touch = e.touches ? e.touches[0] : e;
    pressState.startX = touch.clientX;
    pressState.startY = touch.clientY;
    state.iconPressStart = { x: touch.clientX, y: touch.clientY, iconEl };
    
    pressState.hasDragged = false;
    pressState.isPressed = true;
    pressState.dragStartTime = Date.now();
    
    clearTimeout(state.longPressTimer);
    clearTimeout(pressState.pressTimer);
    
    // Schedule preview ghost (80ms)
    pressState.pressTimer = setTimeout(() => {
        if (!pressState.isPressed || pressState.hasDragged || state.isDraggingIcon) return;
        
        stopScreenDragging();
        
        const iconId = parseInt(iconEl.dataset.iconId);
        const icon = window.icons.find(ic => ic.id === iconId);
        if (!icon) return;
        
        state.dragGhost = createDragGhost(iconEl);
        iconEl.closest('.icon-slot').classList.add('drag-placeholder');
    }, PREVIEW_DELAY);
    
    // Schedule long press (1500ms)
    state.longPressTimer = setTimeout(() => {
        if (!pressState.isPressed || pressState.hasDragged || state.isDraggingIcon) return;
        
        const iconId = parseInt(iconEl.dataset.iconId);
        const icon = window.icons.find(ic => ic.id === iconId);
        if (!icon) return;
        
        handleLongPress(icon, iconEl);
    }, state.config.longPressDuration);
}

function handleMove(e, iconEl, pressState) {
    if (!pressState.isPressed) return;
    
    const touch = e.touches ? e.touches[0] : e;
    const dx = touch.clientX - pressState.startX;
    const dy = touch.clientY - pressState.startY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const timeSinceStart = Date.now() - pressState.dragStartTime;
    
    // Quick swipe detection - hand off to screen scrolling
    if (distance > SWIPE_DISTANCE && timeSinceStart < SWIPE_TIME_THRESHOLD && !pressState.hasDragged) {
        pressState.isPressed = false;
        pressState.hasDragged = true;
        clearTimeout(state.longPressTimer);
        clearTimeout(pressState.pressTimer);
        
        cleanupDragGhost(iconEl);
        
        // Hand off to screen drag
        enableScreenDragging();
        if (state.openFolder) {
            state.folderDragState.startX = pressState.startX;
            state.folderDragState.currentX = touch.clientX;
        }
        return;
    }
    
    // If ghost exists (preview shown), start dragging on movement
    if (state.dragGhost && distance > 5 && !state.isDraggingIcon && !pressState.hasDragged) {
        pressState.hasDragged = true;
        clearTimeout(state.longPressTimer);
        clearTimeout(pressState.pressTimer);
        
        // CRITICAL: Prevent default for touch events to stop scrolling
        if (e.touches) {
            e.preventDefault();
        }
        
        const iconId = parseInt(iconEl.dataset.iconId);
        const icon = window.icons.find(ic => ic.id === iconId);
        if (icon) {
            initiateDrag(icon, iconEl, touch);
        }
    }
    
    // Continue preventing default while dragging
    if (state.isDraggingIcon && e.touches) {
        e.preventDefault();
    }
}

function handleRelease(e, iconEl, pressState) {
    // Ignore right-click mouseup events
    if (pressState.wasRightClick || e.button === 2) {
        pressState.wasRightClick = false;
        return;
    }
    
    const wasPressed = pressState.pressedOnThisIcon;
    
    // Cleanup
    state.iconPressStart = null;
    pressState.isPressed = false;
    state.isInteractingWithIcon = false;
    clearTimeout(state.longPressTimer);
    clearTimeout(pressState.pressTimer);
    
    stopScreenDragging();
    
    // Clean up preview ghost if drag never started
    if (state.dragGhost && !state.isDraggingIcon) {
        cleanupDragGhost(iconEl);
        // Continue to process as click
    }
    
    // Handle as click if no drag occurred
    if (!state.isDraggingIcon && wasPressed) {
        e.stopPropagation();
        e.preventDefault();
        
        const iconId = parseInt(iconEl.dataset.iconId);
        const icon = window.icons.find(ic => ic.id === iconId);
        if (icon) {
            handleIconClick(icon);
        }
    }
    
    // Reset
    pressState.hasDragged = false;
    pressState.pressedOnThisIcon = false;
}

function handleLeave(iconEl, pressState) {
    pressState.isPressed = false;
    clearTimeout(state.longPressTimer);
    clearTimeout(pressState.pressTimer);
}

function handleContextMenu(e, iconEl) {
    e.preventDefault();
    e.stopPropagation();
    
    const iconId = parseInt(iconEl.dataset.iconId);
    const icon = window.icons.find(ic => ic.id === iconId);
    if (!icon) return;
    
    // Store mouse position
    const mouseX = e.clientX;
    const mouseY = e.clientY;
    
    // Import keyboard modules and show context menu
    Promise.all([
        import('./apps-kb-cursor.js'),
        import('./apps-kb-context-menu.js'),
        import('./apps-kb-helpers.js'),
        import('./apps-render.js'),
        import('./apps-kb-state.js'),
        import('./apps-navigation.js')
    ]).then(([cursorModule, contextMenuModule, helpers, render, kbState, nav]) => {
        // Get the icon's position
        const layout = render.calculateLayout();
        const isFolder = !!state.openFolder;
        const visualPos = helpers.regionPositionToRankFile(icon.region, icon.position, layout, isFolder);
        
        console.log('Right-click on icon:', icon.id, 'at position:', visualPos);
        
        // Navigate to the correct screen if needed
        if (visualPos.screen !== undefined) {
            if (isFolder && visualPos.screen !== state.currentFolderScreen) {
                nav.goToFolderScreen(visualPos.screen);
            } else if (!isFolder && visualPos.screen !== state.currentScreen) {
                nav.goToScreen(visualPos.screen);
            }
        }
        
        // Show virtual cursor at icon position
        cursorModule.showVirtualCursor(visualPos.rank, visualPos.file);
        
        // Hide the arrow but keep the outline by removing just the arrow element
        const arrow = document.getElementById('virtualCursorArrow');
        if (arrow) {
            arrow.remove();
        }
        
        // Show context menu at mouse position
        setTimeout(() => {
            console.log('Calling showContextMenu after timeout');
            contextMenuModule.showContextMenu(mouseX, mouseY);
        }, 50);
    });
}

// Global viewport context menu handler
export function attachViewportContextMenu() {
    // Attach to viewport
    const viewport = document.getElementById('appspaceViewport');
    if (viewport) {
        viewport.addEventListener('contextmenu', handleViewportContextMenu);
    }
    
    // Attach to folder overlay
    const folderOverlay = document.getElementById('folderOverlay');
    if (folderOverlay) {
        folderOverlay.addEventListener('contextmenu', handleViewportContextMenu);
    }
}

function handleViewportContextMenu(e) {
    // Check if the target is an icon or inside an icon
    if (e.target.closest('.appicon')) {
        // Let the icon's context menu handler deal with it
        return;
    }
    
    e.preventDefault();
    e.stopPropagation();
    
    const mouseX = e.clientX;
    const mouseY = e.clientY;
    
    import('./apps-kb-context-menu.js').then(contextMenuModule => {
        contextMenuModule.showGlobalContextMenu(mouseX, mouseY);
    });
}
